import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lock,
  ShieldAlert,
  ArrowRight,
  Mail,
  HelpCircle,
  ArrowLeft,
} from "lucide-react";
import { LINKS, COMPANY } from "@/config/links";

// A dummy locked login. Nothing authenticates. Every submit fails.
// No real auth, no user table, no persistence.

const ERROR_TEXT =
  "We couldn't verify that Client ID. If you're an existing client, use the ID in your welcome email or email info@linehaulbooks.com. Don't have one? You're not in the portal yet.";

export default function Portal() {
  const [clientId, setClientId] = useState("");
  const [email, setEmail] = useState("");
  const [showError, setShowError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [view, setView] = useState("login"); // 'login' | 'forgot' | 'not-a-client'

  const onSubmit = (e) => {
    e.preventDefault();
    if (!clientId.trim()) return;
    setSubmitting(true);
    setShowError(false);
    // Simulate an auth check that always fails.
    setTimeout(() => {
      setShowError(true);
      setSubmitting(false);
    }, 700);
  };

  return (
    <div data-testid="page-portal" className="min-h-screen bg-[#040914] text-white flex flex-col">
      {/* Minimal chrome header — not the main nav */}
      <header className="border-b border-white/5 bg-[#040914]/90 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="portal-logo-link" className="flex items-center gap-2.5 group">
            <span className="h-9 w-9 rounded-xl bg-[#00274C] border border-white/10 grid place-items-center group-hover:border-[#FFCB05]/50 transition-colors">
              <Lock className="h-4 w-4 text-[#FFCB05]" strokeWidth={2.25} />
            </span>
            <span className="font-display text-base sm:text-lg font-semibold tracking-tight text-white">
              Linehaul <span className="text-[#FFCB05]">Books</span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="portal-back-link"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium text-[#9CA3AF] hover:text-white border border-white/10 hover:border-[#FFCB05]/40 transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to site
          </Link>
        </div>
      </header>

      {/* Grid pattern background */}
      <div className="absolute inset-x-0 top-16 h-[520px] gradient-radial-blue pointer-events-none" />

      <main className="flex-1 flex items-center justify-center px-6 py-16 relative">
        <div className="w-full max-w-md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="rounded-3xl border border-white/10 bg-[#0A111E] shadow-2xl shadow-black/40 overflow-hidden"
          >
            {/* Header strip */}
            <div className="px-8 pt-8 pb-6 border-b border-white/5">
              <div className="flex items-center gap-2 text-[10px] font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">
                <Lock className="h-3.5 w-3.5" /> Existing clients only
              </div>
              <h1 className="mt-3 font-display text-3xl font-medium tracking-tight text-white" data-testid="portal-heading">
                {view === "forgot"
                  ? "Forgot Client ID?"
                  : view === "not-a-client"
                  ? "Not a client yet?"
                  : "Client portal"}
              </h1>
              <p className="mt-1.5 text-sm text-[#9CA3AF]">
                {view === "forgot"
                  ? "How to retrieve your Client ID."
                  : view === "not-a-client"
                  ? "Everyone starts with a 30-minute call."
                  : "Sign in with your Linehaul Client ID."}
              </p>
            </div>

            <AnimatePresence mode="wait">
              {view === "login" && (
                <motion.div
                  key="login"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <form onSubmit={onSubmit} data-testid="portal-form" className="px-8 py-7 space-y-5">
                    <div>
                      <label htmlFor="client-id" className="block text-xs font-semibold tracking-[0.12em] uppercase text-[#9CA3AF] mb-2">
                        Client ID# <span className="text-[#FFCB05]">*</span>
                      </label>
                      <input
                        id="client-id"
                        type="text"
                        value={clientId}
                        onChange={(e) => { setClientId(e.target.value); setShowError(false); }}
                        placeholder="LHB-XXXX"
                        autoComplete="off"
                        required
                        data-testid="portal-input-client-id"
                        className="w-full rounded-xl bg-[#040914] border border-white/10 px-4 py-3.5 text-base text-white placeholder:text-[#6B7280] focus:outline-none focus:border-[#FFCB05]/50 focus:ring-2 focus:ring-[#FFCB05]/20 transition-colors tracking-wider font-mono"
                      />
                      <p className="mt-2 text-xs text-[#6B7280]">This is the ID in your Linehaul welcome email &mdash; not your USDOT or MC.</p>
                    </div>

                    <div>
                      <label htmlFor="portal-email" className="block text-xs font-semibold tracking-[0.12em] uppercase text-[#9CA3AF] mb-2">
                        Email <span className="text-[#6B7280] font-normal normal-case tracking-normal">(optional)</span>
                      </label>
                      <input
                        id="portal-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        autoComplete="off"
                        data-testid="portal-input-email"
                        className="w-full rounded-xl bg-[#040914] border border-white/10 px-4 py-3.5 text-sm text-white placeholder:text-[#6B7280] focus:outline-none focus:border-[#FFCB05]/50 focus:ring-2 focus:ring-[#FFCB05]/20 transition-colors"
                      />
                    </div>

                    <AnimatePresence>
                      {showError && (
                        <motion.div
                          initial={{ opacity: 0, y: -6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0 }}
                          data-testid="portal-error"
                          className="flex items-start gap-3 rounded-xl border border-red-500/30 bg-red-500/5 p-4"
                        >
                          <ShieldAlert className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
                          <p className="text-xs text-red-200 leading-relaxed">
                            {ERROR_TEXT}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <button
                      type="submit"
                      disabled={submitting || !clientId.trim()}
                      data-testid="portal-submit-btn"
                      className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold bg-[#FFCB05] text-[#040914] hover:bg-[#FFD633] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                    >
                      {submitting ? "Verifying…" : "Sign in"}
                      {!submitting && <ArrowRight className="h-4 w-4" />}
                    </button>

                    <div className="flex items-center justify-between pt-2 text-xs">
                      <button
                        type="button"
                        onClick={() => setView("forgot")}
                        data-testid="portal-forgot-link"
                        className="inline-flex items-center gap-1.5 text-[#9CA3AF] hover:text-[#FFCB05] transition-colors"
                      >
                        <HelpCircle className="h-3.5 w-3.5" /> Forgot Client ID?
                      </button>
                      <button
                        type="button"
                        onClick={() => setView("not-a-client")}
                        data-testid="portal-not-client-link"
                        className="text-[#9CA3AF] hover:text-[#FFCB05] transition-colors"
                      >
                        Not a client yet?
                      </button>
                    </div>
                  </form>
                </motion.div>
              )}

              {view === "forgot" && (
                <motion.div
                  key="forgot"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  data-testid="portal-forgot-view"
                  className="px-8 py-7"
                >
                  <div className="flex items-start gap-3 rounded-xl border border-white/10 bg-[#040914] p-4">
                    <Mail className="h-4 w-4 text-[#FFCB05] mt-0.5 shrink-0" />
                    <p className="text-sm text-[#E5E7EB] leading-relaxed">
                      Client IDs are issued after your engagement is signed. Email{" "}
                      <a href={`mailto:${COMPANY.email}`} className="text-[#FFCB05] hover:underline">
                        {COMPANY.email}
                      </a>{" "}
                      from the address on file and we’ll resend yours.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setView("login"); setShowError(false); }}
                    data-testid="portal-forgot-back"
                    className="mt-6 inline-flex items-center gap-2 text-xs font-semibold text-[#9CA3AF] hover:text-white transition-colors"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
                  </button>
                </motion.div>
              )}

              {view === "not-a-client" && (
                <motion.div
                  key="not-a-client"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  data-testid="portal-not-client-view"
                  className="px-8 py-7"
                >
                  <p className="text-sm text-[#E5E7EB] leading-relaxed">
                    There is no public registration. Every engagement starts with a 30-minute call.
                  </p>
                  <div className="mt-5 space-y-2.5">
                    <a
                      href={LINKS.discoveryCall}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="portal-not-client-calendly"
                      className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold bg-[#FFCB05] text-[#040914] hover:bg-[#FFD633] transition-colors"
                    >
                      See if Linehaul is a fit (30 min) <ArrowRight className="h-4 w-4" />
                    </a>
                    <Link
                      to="/contact"
                      data-testid="portal-not-client-contact"
                      className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold border border-white/15 text-white hover:border-[#FFCB05]/50 hover:bg-white/5 transition-all"
                    >
                      Contact us
                    </Link>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setView("login"); setShowError(false); }}
                    data-testid="portal-not-client-back"
                    className="mt-6 inline-flex items-center gap-2 text-xs font-semibold text-[#9CA3AF] hover:text-white transition-colors"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Footer note */}
          <p className="mt-6 text-center text-[11px] text-[#6B7280] leading-relaxed">
            This portal is secured. Sign-in attempts are logged.{" "}
            <a href={`mailto:${COMPANY.email}`} className="text-[#9CA3AF] hover:text-[#FFCB05] transition-colors">
              {COMPANY.email}
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}
